import React, { useState, useEffect } from 'react';
import '../css/ourapproach.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheckCircle, faLightbulb, faShieldAlt, faHandshake, faBookOpen, faCogs, faBalanceScale, faHeart, faUsers,
    faCalendarAlt, faSmile, faUserFriends, faQuestionCircle, faCommentAlt, faEye, faLifeRing, faComments
} from '@fortawesome/free-solid-svg-icons';
import visionimg from '../assets/visionimg.webp';
import missionimg from '../assets/missionimg.webp';
import valuesimg from '../assets/valuesimg.webp';
import purposeimg from '../assets/purposeimg.webp';
import promiseimg from '../assets/promiseimg.webp';
import approachimg from '../assets/approachimg.webp';
import visioniconimg from '../assets/visioniconimg.webp';
import missioniconimg from '../assets/missioniconimg.webp';
import valuesiconimg from '../assets/valuesiconimg.webp';
import purposeiconimg from '../assets/purposeiconimg.webp';
import promiseiconimg from '../assets/promiseiconimg.webp';
import approachiconimg from '../assets/approachiconimg.webp';
import { Helmet } from 'react-helmet-async';

const sidebarContentData = [
    {
        id: 'vision',
        name: 'Vision',
        icon: '🔭',
        mainImage: visionimg,
        cardIcon: visioniconimg,
        title: 'Empowering Every Indian',
        description: 'We envision an India where every individual understands insurance and makes informed, confident decisions—without confusion or fear.',
        features: [
            { text: 'Insurance for All', faIcon: faShieldAlt },
            { text: 'Financial Literacy First', faIcon: faLightbulb },
            { text: 'Informed Decision-Making', faIcon: faCheckCircle },
            { text: 'Long-Term Empowerment', faIcon: faCalendarAlt }
        ]
    },
    {
        id: 'mission',
        name: 'Mission',
        icon: '🎯',
        mainImage: missionimg,
        cardIcon: missioniconimg,
        title: 'Simplify, Educate, Enable',
        description: 'To simplify insurance knowledge, educate users through expert tools and content, and enable smart, transparent policy choices at scale.',
        features: [
            { text: 'Insurance Made Simple', faIcon: faCogs },
            { text: 'Affordable Expert Access', faIcon: faUsers },
            { text: 'Tool-Driven Education', faIcon: faBookOpen },
            { text: 'Zero-Jargon Approach', faIcon: faCommentAlt }
        ]
    },
    {
        id: 'values',
        name: 'Values',
        icon: '🧭',
        mainImage: valuesimg,
        cardIcon: valuesiconimg,
        title: 'Guided by Trust',
        description: 'Our values are built on trust, empathy, clarity, and responsibility—we serve with your best interests at heart, always.',
        features: [
            { text: 'Trust Over Sales', faIcon: faHandshake },
            { text: 'Empathy in Advice', faIcon: faHeart },
            { text: 'Clarity in Language', faIcon: faLightbulb },
            { text: 'Responsibility in Service', faIcon: faUsers }
        ]
    },
    {
        id: 'purpose',
        name: 'Purpose',
        icon: '🔍',
        mainImage: purposeimg,
        cardIcon: purposeiconimg,
        title: 'Demystifying Insurance Forever',
        description: 'Our core purpose is to eliminate fear and confusion around insurance by turning complexity into clarity for every Indian household.',
        features: [
            { text: 'Understand, Don’t Guess', faIcon: faQuestionCircle },
            { text: 'Policy Literacy Drive', faIcon: faBookOpen },
            { text: 'Everyday Language', faIcon: faCommentAlt },
            { text: 'People Over Products', faIcon: faUsers }
        ]
    },
    {
        id: 'promise',
        name: 'Promise',
        icon: '🤝',
        mainImage: promiseimg,
        cardIcon: promiseiconimg,
        title: 'We’ve Got You',
        description: 'We promise to stand by you—whether you’re comparing plans or reviewing documents—no tricks, no jargon, just real help.',
        features: [
            { text: 'Transparent Help', faIcon: faEye },
            { text: 'Unbiased Guidance', faIcon: faBalanceScale },
            { text: 'Fast & Friendly', faIcon: faSmile },
            { text: 'Always On Your Side', faIcon: faLifeRing }
        ]
    },
    {
        id: 'approach',
        name: 'Approach',
        icon: '🧠',
        mainImage: approachimg,
        cardIcon: approachiconimg,
        title: 'Smart, Human, Simple',
        description: 'Our approach blends smart tech, expert humans, and simple UX to make insurance less boring and more empowering.',
        features: [
            { text: 'Tools That Guide', faIcon: faCogs },
            { text: 'Experts That Listen', faIcon: faUserFriends },
            { text: 'UX That Simplifies', faIcon: faLightbulb },
            { text: 'Language That Connects', faIcon: faComments }
        ]
    }
];

const OurApproach = () => {
    const [activeItemId, setActiveItemId] = useState('vision');
    const [isAnimating, setIsAnimating] = useState(false);
    const currentContent = sidebarContentData.find(item => item.id === activeItemId);

    useEffect(() => {
        setIsAnimating(true);
        const timer = setTimeout(() => {
            setIsAnimating(false);
        }, 300);
        return () => clearTimeout(timer);
    }, [activeItemId]);

    if (!currentContent) {
        return <div>Loading content...</div>;
    }

    return (
        <div className="ourapproach-container">
            <Helmet>
                <title>Our Approach | Simplified Insurance Guidance | Policy Samjho</title>
                <meta
                    name="description"
                    content="Discover how Policy Samjho simplifies insurance. Our expert-led approach focuses on transparency, literacy, and personalized guidance for every Indian household."
                />
                <meta
                    name="keywords"
                    content="insurance guidance approach, how Policy Samjho works, insurance literacy India, simplified insurance process, personalized policy advice, insurance expert consultation"
                />
                <link rel="canonical" href="https://policysamjho.com/our-approach/" />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "WebPage",
                        "name": "Our Approach - Policy Samjho",
                        "url": "https://policysamjho.com/our-approach/",
                        "description": "Policy Samjho provides a unique, jargon-free approach to insurance guidance, empowering users with clarity and expert support.",
                        "publisher": {
                            "@type": "Organization",
                            "name": "Policy Samjho",
                            "url": "https://policysamjho.com/"
                        }
                    })}
                </script>
            </Helmet>
            
            <p className="ourapproach-tagline">&gt;&gt;&gt; UNDERSTAND WITH CONFIDENCE &lt;&lt;&lt;</p>
            <h1 className="ourapproach-title">
                <span className="ourapproach-title-highlight-yellow">Our Approach to Insurance Guidance:</span>
                <span className="ourapproach-title-normal"> Based on your life stage, budget, and coverage goals.</span>
            </h1>

            <div className="ourapproach-main-layout-section">
                <div className="ourapproach-sidebar">
                    <ul className="ourapproach-sidebar-list">
                        {sidebarContentData.map(item => (
                            <li
                                key={item.id}
                                className={`ourapproach-sidebar-item ${activeItemId === item.id ? 'ourapproach-active' : ''}`}
                                onClick={() => setActiveItemId(item.id)}
                            >
                                <span className="ourapproach-sidebar-icon">{item.icon}</span> {item.name}
                            </li>
                        ))}
                    </ul>
                </div>
                <div className={`ourapproach-content-area ${isAnimating ? 'animating-out' : 'animating-in'}`}>
                    <div className="ourapproach-main-content">
                        <div className="ourapproach-card-inner">
                            <div className="ourapproach-card-header">
                                <div className="ourapproach-card-icon-placeholder">
                                    <img src={currentContent.cardIcon} alt={`${currentContent.name} Icon`} className="ourapproach-card-icon" />
                                </div>
                                <h2 className="ourapproach-card-title">{currentContent.title}</h2>
                            </div>
                            <p className="ourapproach-card-description">
                                {currentContent.description}
                            </p>
                            <ul className="ourapproach-card-features-list">
                                {currentContent.features.map((feature, index) => (
                                    <li key={index} className="ourapproach-card-feature-item">
                                        <FontAwesomeIcon icon={feature.faIcon} className="ourapproach-feature-icon" />
                                        {feature.text}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    <div className="ourapproach-image-section">
                        <img src={currentContent.mainImage} alt={`Image for ${currentContent.name}`} className="ourapproach-main-image" />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OurApproach;